/**
 * Pill tabs + mobile carousel pager for tabbed cards.
 * Labels / first-paint scheme / contrast / subtitle are set by the inline script;
 * this element handles interaction, indicator geometry, and panel chrome swaps.
 * Theme editor uses the same switch/hide behavior as the storefront — click a pill
 * to reveal that Tab’s cards/footer (blocks keep shopify_attributes for selection).
 * Mobile pager init also runs outside the CE when carousel is on without tabs.
 */

/**
 * Wire scroll ↔ pager dots for one cards scroller.
 * @param {HTMLElement} scroller
 * @param {HTMLElement} pager
 * @param {AbortSignal} signal
 */
function bindColumnCardsPager(scroller, pager, signal) {
  const dots = Array.from(pager.querySelectorAll('[data-bon-column-cards-pager-dot]'));
  const slides = Array.from(scroller.children).filter((el) => el.nodeType === Node.ELEMENT_NODE);

  if (dots.length === 0 || slides.length === 0) return;

  /** @type {number} */
  let activeIndex = 0;
  /** @type {boolean} */
  let ignoreScrollSync = false;
  /** @type {ReturnType<typeof setTimeout> | null} */
  let scrollSettleTimer = null;
  /** @type {(() => void) | null} */
  let onScrollEnd = null;
  /** @type {number} */
  let programmaticToken = 0;

  /**
   * @param {number} index
   * @param {{ force?: boolean }} [options]
   */
  const setActiveDot = (index, options = {}) => {
    if (index < 0 || index >= dots.length) return;
    if (!options.force && index === activeIndex) return;

    activeIndex = index;

    dots.forEach((dot, i) => {
      const isActive = i === index;
      dot.classList.toggle('is-active', isActive);
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  };

  /**
   * @returns {number}
   */
  const indexFromScroll = () => {
    const scrollLeft = scroller.scrollLeft;
    let closest = 0;
    let closestDist = Infinity;

    slides.forEach((slide, index) => {
      const dist = Math.abs(slide.offsetLeft - scrollLeft);
      if (dist < closestDist) {
        closestDist = dist;
        closest = index;
      }
    });

    return closest;
  };

  /**
   * @param {number} token
   */
  const endProgrammaticScroll = (token) => {
    if (token !== programmaticToken) return;

    if (scrollSettleTimer) {
      clearTimeout(scrollSettleTimer);
      scrollSettleTimer = null;
    }
    if (onScrollEnd) {
      scroller.removeEventListener('scrollend', onScrollEnd);
      onScrollEnd = null;
    }
    scroller.style.scrollSnapType = '';
    ignoreScrollSync = false;
    setActiveDot(indexFromScroll(), { force: true });
  };

  /**
   * @param {number} index
   */
  const scrollToIndex = (index) => {
    const slide = slides[index];
    if (!slide) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    setActiveDot(index, { force: true });

    if (reduceMotion) {
      scroller.scrollTo({ left: slide.offsetLeft, behavior: 'auto' });
      setActiveDot(indexFromScroll(), { force: true });
      return;
    }

    // Hold the active dot through the smooth scroll so intermediate slides
    // do not flicker the pager; disable snap so it does not fight scrollTo.
    const token = ++programmaticToken;
    ignoreScrollSync = true;
    scroller.style.scrollSnapType = 'none';

    if (scrollSettleTimer) clearTimeout(scrollSettleTimer);
    if (onScrollEnd) {
      scroller.removeEventListener('scrollend', onScrollEnd);
      onScrollEnd = null;
    }

    scroller.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });

    onScrollEnd = () => endProgrammaticScroll(token);
    scroller.addEventListener('scrollend', onScrollEnd, { signal });
    // Fallback when scrollend is missing or never fires.
    scrollSettleTimer = setTimeout(() => endProgrammaticScroll(token), 450);
  };

  const onScroll = () => {
    if (ignoreScrollSync) return;
    setActiveDot(indexFromScroll());
  };

  scroller.addEventListener('scroll', onScroll, { passive: true, signal });

  dots.forEach((dot, index) => {
    dot.addEventListener('click', () => scrollToIndex(index), { signal });
  });

  signal.addEventListener(
    'abort',
    () => {
      programmaticToken += 1;
      if (scrollSettleTimer) clearTimeout(scrollSettleTimer);
      if (onScrollEnd) {
        scroller.removeEventListener('scrollend', onScrollEnd);
        onScrollEnd = null;
      }
      scroller.style.scrollSnapType = '';
      ignoreScrollSync = false;
    },
    { once: true }
  );

  setActiveDot(indexFromScroll(), { force: true });
}

/**
 * Reset a panel’s mobile scroller to the first card and sync its pager.
 * @param {HTMLElement} panel
 */
function resetColumnCardsScroller(panel) {
  const scroller = panel.querySelector('[data-bon-column-cards-scroller]');
  if (!scroller) return;

  scroller.scrollLeft = 0;

  const pager = panel.querySelector('[data-bon-column-cards-pager]');
  if (!pager) return;

  const dots = pager.querySelectorAll('[data-bon-column-cards-pager-dot]');
  dots.forEach((dot, i) => {
    const isActive = i === 0;
    dot.classList.toggle('is-active', isActive);
    if (isActive) {
      dot.setAttribute('aria-current', 'true');
    } else {
      dot.removeAttribute('aria-current');
    }
  });
}

/**
 * Init pagers under a root (section or document).
 * @param {ParentNode} root
 * @param {AbortSignal} signal
 */
function initColumnCardsPagers(root, signal) {
  const pagers = root.querySelectorAll('[data-bon-column-cards-pager]');

  for (const pager of pagers) {
    if (!(pager instanceof HTMLElement)) continue;
    if (pager.dataset.pagerBound === 'true') continue;

    const cards = pager.closest('.bon-column-cards__cards');
    const scroller = cards?.querySelector('[data-bon-column-cards-scroller]');
    if (!(scroller instanceof HTMLElement)) continue;

    pager.dataset.pagerBound = 'true';
    bindColumnCardsPager(scroller, pager, signal);

    signal.addEventListener(
      'abort',
      () => {
        delete pager.dataset.pagerBound;
      },
      { once: true }
    );
  }
}

class BonColumnCards extends HTMLElement {
  static FULL_MS = 500;

  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {number} */
  #currentIndex = 0;
  /** @type {boolean} */
  #isTransitioning = false;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #animationTimer = null;
  /** @type {MediaQueryList | null} */
  #reducedMotionQuery = null;
  /** @type {number} */
  #indicatorRetryId = 0;
  /** @type {number} */
  #indicatorRaf = 0;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.sectionEl = this.querySelector('[data-bon-column-cards-section]');
    this.backgroundEl = this.querySelector('[data-bon-column-cards-background]');
    this.tabsContainer = this.querySelector('[data-bon-column-cards-tablist]');
    this.indicator = this.querySelector('[data-bon-column-cards-indicator]');
    this.subtitleHost = this.querySelector('[data-bon-column-cards-subtitle-host]');
    this.panelsEl = this.querySelector('[data-bon-column-cards-panels]');
    this.panels = Array.from(this.querySelectorAll('[data-bon-column-cards-panel]'));
    this.tabs = Array.from(this.querySelectorAll('[data-bon-column-cards-tab]'));

    initColumnCardsPagers(this, signal);

    if (!this.tabsContainer || !this.panelsEl || this.panels.length === 0 || this.tabs.length === 0) {
      return;
    }

    this.#wireAria();
    this.#bindTabs(signal);
    this.#updateTabIndex();
    this.#scheduleIndicatorRefresh();

    const onMeasure = () => this.#queueIndicatorMeasure();
    window.addEventListener('resize', onMeasure, { signal });

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(onMeasure);
      ro.observe(this.tabsContainer);
      signal.addEventListener('abort', () => ro.disconnect());
    }
  }

  disconnectedCallback() {
    if (this.#animationTimer) clearTimeout(this.#animationTimer);
    if (this.#indicatorRaf) cancelAnimationFrame(this.#indicatorRaf);
    this.#abortController?.abort();
    this.#abortController = null;
  }

  #wireAria() {
    this.tabs.forEach((tab, index) => {
      const panel = this.panels[index];
      if (!panel) return;
      // Inline script usually set this already; fill placeholder if module wins the race.
      if (panel.dataset.tabLabel && tab.textContent.trim() === 'Tab') {
        tab.textContent = panel.dataset.tabLabel;
      }
      panel.setAttribute('aria-labelledby', tab.id);
    });
  }

  /**
   * @param {AbortSignal} signal
   */
  #bindTabs(signal) {
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.#switchTab(index), { signal });
    });

    this.tabsContainer.addEventListener('keydown', (event) => this.#handleKeyboard(event), { signal });
  }

  /**
   * @param {number} newIndex
   * @param {'forward' | 'backward' | null} [direction]
   */
  #switchTab(newIndex, direction = null) {
    if (newIndex === this.#currentIndex || newIndex < 0 || newIndex >= this.panels.length) return;

    const skipEnter = this.#isTransitioning || Boolean(this.#reducedMotionQuery?.matches);
    const settleMs = skipEnter ? 0 : BonColumnCards.FULL_MS;

    if (this.#animationTimer) clearTimeout(this.#animationTimer);
    this.#isTransitioning = true;

    const oldIndex = this.#currentIndex;
    this.#currentIndex = newIndex;

    if (direction === null) {
      direction = newIndex > oldIndex ? 'forward' : 'backward';
    }

    this.panels.forEach((panel) => {
      panel.classList.remove('is-animating');
      const grid = panel.querySelector('.bon-column-cards__cards-grid');
      grid?.classList.remove(
        'bon-column-cards__cards-grid--slide-left-enter',
        'bon-column-cards__cards-grid--slide-right-enter'
      );
    });

    this.panelsEl.classList.remove('is-soft-transition');

    const oldTab = this.tabs[oldIndex];
    const newTab = this.tabs[newIndex];
    oldTab.classList.remove('bon-column-cards__tab--active');
    oldTab.setAttribute('aria-selected', 'false');
    oldTab.setAttribute('tabindex', '-1');
    newTab.classList.add('bon-column-cards__tab--active');
    newTab.setAttribute('aria-selected', 'true');
    newTab.setAttribute('tabindex', '0');

    // CSS transitions left/top/width/height — one measure is enough.
    this.#updateIndicatorPosition();
    this.#applyPanelChrome(newIndex);

    const oldPanel = this.panels[oldIndex];
    const newPanel = this.panels[newIndex];
    const newGrid = newPanel.querySelector('.bon-column-cards__cards-grid');

    const enterClass =
      direction === 'forward'
        ? 'bon-column-cards__cards-grid--slide-left-enter'
        : 'bon-column-cards__cards-grid--slide-right-enter';

    const activeEl = document.activeElement;
    const focusWasInOldPanel = Boolean(activeEl && (activeEl === oldPanel || oldPanel.contains(activeEl)));
    const focusWasInContainer = Boolean(activeEl && this.contains(activeEl));

    oldPanel.classList.remove('bon-column-cards-panel--active');
    oldPanel.setAttribute('hidden', '');
    oldPanel.classList.remove('is-animating');

    // Enter motion is on the cards grid (clipped by `.bon-column-cards__cards`) — footer is not animated.
    if (!skipEnter && newGrid) {
      newGrid.classList.add(enterClass);
      newPanel.classList.add('is-animating');
    }

    newPanel.removeAttribute('hidden');
    this.#setPanelInteractive(oldPanel, false);
    this.#setPanelInteractive(newPanel, true);
    resetColumnCardsScroller(newPanel);

    if (focusWasInOldPanel || (focusWasInContainer && !this.contains(document.activeElement))) {
      newTab.focus({ preventScroll: true });
    }

    newPanel.offsetHeight;

    requestAnimationFrame(() => {
      newPanel.classList.add('bon-column-cards-panel--active');
    });

    this.#animationTimer = setTimeout(() => {
      if (!skipEnter && newGrid) {
        newGrid.classList.remove(enterClass);
      }
      newPanel.classList.remove('is-animating');
      this.#isTransitioning = false;
    }, settleMs);
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyboard(event) {
    const { key } = event;
    let newIndex = this.#currentIndex;

    switch (key) {
      case 'ArrowLeft':
        event.preventDefault();
        newIndex = this.#currentIndex > 0 ? this.#currentIndex - 1 : this.tabs.length - 1;
        this.#switchTab(newIndex, 'backward');
        this.tabs[newIndex].focus({ preventScroll: true });
        break;
      case 'ArrowRight':
        event.preventDefault();
        newIndex = this.#currentIndex < this.tabs.length - 1 ? this.#currentIndex + 1 : 0;
        this.#switchTab(newIndex, 'forward');
        this.tabs[newIndex].focus({ preventScroll: true });
        break;
      case 'Home':
        event.preventDefault();
        this.#switchTab(0, 'backward');
        this.tabs[0].focus({ preventScroll: true });
        break;
      case 'End':
        event.preventDefault();
        newIndex = this.tabs.length - 1;
        this.#switchTab(newIndex, 'forward');
        this.tabs[newIndex].focus({ preventScroll: true });
        break;
      default:
        break;
    }
  }

  #updateTabIndex() {
    this.panels.forEach((panel, index) => {
      this.#setPanelInteractive(panel, index === this.#currentIndex);
    });
  }

  /**
   * @param {HTMLElement} panel
   * @param {boolean} isActive
   */
  #setPanelInteractive(panel, isActive) {
    panel.setAttribute('tabindex', isActive ? '0' : '-1');
    panel.querySelectorAll('a, button, input, select, textarea').forEach((el) => {
      if (isActive) {
        el.removeAttribute('tabindex');
      } else {
        el.setAttribute('tabindex', '-1');
      }
    });
  }

  /**
   * Color scheme, tab-track contrast, and subtitle host for the active panel.
   * @param {number} index
   */
  #applyPanelChrome(index) {
    const panel = this.panels[index];
    if (!panel) return;

    const scheme = panel.dataset.colorScheme;
    if (scheme) {
      const schemeClass = `color-${scheme}`;
      [this.sectionEl, this.backgroundEl].forEach((el) => {
        if (!el) return;
        [...el.classList].forEach((cls) => {
          if (cls.startsWith('color-')) el.classList.remove(cls);
        });
        el.classList.add(schemeClass);
      });
    }

    const contrast = panel.dataset.tablistTrackContrast;
    if (this.tabsContainer) {
      if (contrast) {
        this.tabsContainer.style.setProperty('--bon-column-cards-tablist-contrast', contrast);
      } else {
        this.tabsContainer.style.removeProperty('--bon-column-cards-tablist-contrast');
      }
    }

    this.#syncSubtitleHost(panel);
  }

  /**
   * @param {HTMLElement} panel
   */
  #syncSubtitleHost(panel) {
    if (!this.subtitleHost) return;
    const source = panel.querySelector('.bon-column-cards-panel__subtitle');
    if (!source) {
      this.subtitleHost.replaceChildren();
      return;
    }
    this.subtitleHost.replaceChildren(
      ...Array.from(source.childNodes).map((node) => node.cloneNode(true))
    );
  }

  #queueIndicatorMeasure() {
    if (this.#indicatorRaf) return;
    this.#indicatorRaf = requestAnimationFrame(() => {
      this.#indicatorRaf = 0;
      this.#updateIndicatorPosition();
    });
  }

  #updateIndicatorPosition() {
    const activeTab = this.tabs[this.#currentIndex] ?? this.querySelector('.bon-column-cards__tab--active');
    if (!activeTab || !this.indicator || !this.tabsContainer) return;

    const tabRect = activeTab.getBoundingClientRect();
    const containerRect = this.tabsContainer.getBoundingClientRect();

    if (tabRect.width < 1 || tabRect.height < 1 || containerRect.width < 1) return;

    this.indicator.style.left = `${tabRect.left - containerRect.left + this.tabsContainer.scrollLeft}px`;
    this.indicator.style.top = `${tabRect.top - containerRect.top}px`;
    this.indicator.style.width = `${tabRect.width}px`;
    this.indicator.style.height = `${tabRect.height}px`;
  }

  #scheduleIndicatorRefresh() {
    const id = ++this.#indicatorRetryId;
    const attempt = () => {
      if (this.#indicatorRetryId !== id) return;
      this.#updateIndicatorPosition();
    };

    requestAnimationFrame(attempt);

    if (document.fonts?.ready) {
      document.fonts.ready.then(attempt);
    }
  }
}

if (!customElements.get('bon-column-cards')) {
  customElements.define('bon-column-cards', BonColumnCards);
}

// Carousel-only instances (no tabs CE): bind pagers at module load.
const standalonePagerController = new AbortController();
initColumnCardsPagers(document, standalonePagerController.signal);
